import { Draft } from "./inboxDraft.types";

interface ShouldShowInlineGenerateBarParams {
	hasConversations: boolean;
	drafts: Draft[];
}

export const shouldShowInlineGenerateBar = ({
	hasConversations,
	drafts,
}: ShouldShowInlineGenerateBarParams): boolean => {
	if (hasConversations) {
		return false;
	}

	return !drafts.some((draft) => draft.content.trim().length > 0);
};

/**
 * The exact complement of {@link shouldShowInlineGenerateBar}: whenever the full
 * inline "Tell AI" bar is hidden (a conversation exists and/or a draft already has
 * content), a collapsed strip pinned above the draft editor keeps the AI entry
 * point reachable instead of disappearing entirely (PREC-3697).
 */
export const shouldShowCollapsedGenerateStrip = (
	params: ShouldShowInlineGenerateBarParams
): boolean => !shouldShowInlineGenerateBar(params);

interface GenerateEntryVisibilityParams extends ShouldShowInlineGenerateBarParams {
	isCopywritingActive: boolean;
	collapsedGenerateBar: boolean;
	// Render no "Tell AI" entry at all (neither bar nor strip) — the Inbox host hides it
	// (PREC-3850); the editor's overflow menu keeps AI writing reachable there.
	hideGenerateBar?: boolean;
}

/**
 * Resolves which "Tell AI" entry the chat log renders: the full inline bar for an
 * empty chat, or the collapsed strip when the bar is hidden or the host asked for
 * the collapsed manual-write entry. With copywriting inactive or the host hiding
 * the entry entirely, neither shows.
 */
export const resolveGenerateEntryVisibility = ({
	isCopywritingActive,
	collapsedGenerateBar,
	hideGenerateBar = false,
	...params
}: GenerateEntryVisibilityParams): { showInlineBar: boolean; showCollapsedStrip: boolean } => ({
	showInlineBar:
		!hideGenerateBar &&
		isCopywritingActive &&
		!collapsedGenerateBar &&
		shouldShowInlineGenerateBar(params),
	showCollapsedStrip:
		!hideGenerateBar &&
		isCopywritingActive &&
		(collapsedGenerateBar || shouldShowCollapsedGenerateStrip(params)),
});
